import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiAlertCircle, FiCheckCircle, FiInfo, FiRefreshCw, FiChevronDown, FiChevronUp, FiTarget } from 'react-icons/fi';
import { FaWineGlass, FaFlask, FaBalanceScale, FaPercentage, FaSyncAlt } from 'react-icons/fa';
import { PageHeader } from '../components/ui';

interface BaseWine {
  id: string;
  name: string;
  alcohol: number;
  pH: number;
  totalAcidity: number;
  residualSugar: number;
  percentage: number;
  color: string;
  isExpanded: boolean;
}

interface BlendResult {
  alcohol: number;
  pH: number;
  totalAcidity: number;
  residualSugar: number;
}

// Wine color palette
const wineColors = [
  'bg-red-400', 'bg-purple-400', 'bg-primary-400', 'bg-pink-400',
  'bg-secondary-400', 'bg-warning-400', 'bg-success-500', 'bg-info-500',
];

const WineBlendCalculator: React.FC = () => {
  const [baseWines, setBaseWines] = useState<BaseWine[]>([
    {
      id: '1',
      name: '',
      alcohol: 0,
      pH: 0,
      totalAcidity: 0,
      residualSugar: 0,
      percentage: 0,
      color: wineColors[0],
      isExpanded: true
    }
  ]);

  const [blendResult, setBlendResult] = useState<BlendResult>({
    alcohol: 0,
    pH: 0,
    totalAcidity: 0,
    residualSugar: 0
  });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [totalPercentage, setTotalPercentage] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  // Calculate the total percentage and blend results when baseWines change
  useEffect(() => {
    const total = baseWines.reduce((sum, wine) => sum + (Number(wine.percentage) || 0), 0);
    setTotalPercentage(total);

    if (total !== 100 && total !== 0) {
      setValidationError('Total percentage must equal 100%');
    } else {
      setValidationError(null);
    }

    const computeBlendResults = () => {
      if (total === 0) {
        setBlendResult({
          alcohol: 0,
          pH: 0,
          totalAcidity: 0,
          residualSugar: 0
        });
        return;
      }

      const result = {
        alcohol: baseWines.reduce((sum, wine) => sum + (Number(wine.alcohol) * (Number(wine.percentage) / 100) || 0), 0),
        pH: baseWines.reduce((sum, wine) => sum + (Number(wine.pH) * (Number(wine.percentage) / 100) || 0), 0),
        totalAcidity: baseWines.reduce((sum, wine) => sum + (Number(wine.totalAcidity) * (Number(wine.percentage) / 100) || 0), 0),
        residualSugar: baseWines.reduce((sum, wine) => sum + (Number(wine.residualSugar) * (Number(wine.percentage) / 100) || 0), 0)
      };

      setBlendResult(result);
    };

    if (baseWines.some((wine) => wine.percentage > 0)) {
      setIsCalculating(true);
      const timer = setTimeout(() => {
        computeBlendResults();
        setIsCalculating(false);
      }, 300);
      return () => clearTimeout(timer);
    }

    computeBlendResults();
  }, [baseWines]);

  // Handler for updating a wine property
  const handleWineChange = (index: number, field: keyof BaseWine, value: string | number) => {
    const updatedWines = [...baseWines];
    const updatedWine = { ...updatedWines[index] };

    // Handle different field types correctly
    if (field === 'name') {
      updatedWine.name = value as string;
    } else if (field === 'id') {
      updatedWine.id = value as string;
    } else if (field === 'color') {
      updatedWine.color = value as string;
    } else if (field === 'isExpanded') {
      updatedWine.isExpanded = Boolean(value);
    } else {
      // For numeric fields (alcohol, pH, totalAcidity, residualSugar, percentage)
      const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      updatedWine[field] = numValue as never; // Use type assertion to bypass strict type checking
    }

    updatedWines[index] = updatedWine;
    setBaseWines(updatedWines);
  };

  // Add a new base wine
  const addWine = () => {
    const newWineIndex = baseWines.length;
    const colorIndex = newWineIndex % wineColors.length;

    const newWine: BaseWine = {
      id: `${Date.now()}`,
      name: '',
      alcohol: 0,
      pH: 0,
      totalAcidity: 0,
      residualSugar: 0,
      percentage: 0,
      color: wineColors[colorIndex],
      isExpanded: true
    };

    // Collapse previous wines when adding a new one
    const updatedWines = baseWines.map(wine => ({
      ...wine,
      isExpanded: false
    }));

    setBaseWines([...updatedWines, newWine]);
  };

  // Toggle wine component expansion
  const toggleWineExpansion = (index: number) => {
    const updatedWines = [...baseWines];
    updatedWines[index] = {
      ...updatedWines[index],
      isExpanded: !updatedWines[index].isExpanded
    };
    setBaseWines(updatedWines);
  };

  // Remove a base wine by index
  const removeWine = (index: number) => {
    if (baseWines.length <= 1) {
      return; // Keep at least one wine
    }

    const updatedWines = baseWines.filter((_, i) => i !== index);
    setBaseWines(updatedWines);
  };

  // Reset all input fields
  const handleReset = () => {
    setBaseWines([
      {
        id: '1',
        name: '',
        alcohol: 0,
        pH: 0,
        totalAcidity: 0,
        residualSugar: 0,
        percentage: 0,
        color: wineColors[0],
        isExpanded: true
      }
    ]);

    setBlendResult({
      alcohol: 0,
      pH: 0,
      totalAcidity: 0,
      residualSugar: 0
    });

    setValidationError(null);
    setIsCalculating(false);
  };

  // Check if the blend is valid
  const isBlendValid = totalPercentage === 100;

  // Distribute percentages evenly
  const distributePercentages = () => {
    if (baseWines.length === 0) return;

    const evenPercentage = 100 / baseWines.length;
    const updatedWines = baseWines.map(wine => ({
      ...wine,
      percentage: Number(evenPercentage.toFixed(1)) // Round to 1 decimal place
    }));

    setBaseWines(updatedWines);
  };

  // Get classnames for progress bar
  const getProgressBarClasses = () => {
    if (totalPercentage === 0) return 'bg-neutral-300';
    if (totalPercentage < 100) return 'bg-yellow-500';
    if (totalPercentage === 100) return 'bg-green-500';
    return 'bg-red-500'; // Over 100%
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wine Blend Calculator"
        subtitle="Calculate the characteristics of your wine blend."
        icon={FaWineGlass({ className: "h-5 w-5" })}
      />

      {/* Percentage Progress Bar */}
      <div className="mb-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {FaPercentage({ className: "mr-2 text-primary-500" })}
              <h3 className="text-lg font-medium text-monday-text">Blend Composition</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={distributePercentages}
                className="inline-flex items-center px-2 py-1 border border-monday-border shadow-sm text-xs leading-4 font-medium rounded-md text-monday-text bg-white hover:bg-monday-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200 transition-colors duration-150"
                title="Distribute percentages evenly"
              >
                {FiTarget({ className: "mr-1" })}
                Distribute
              </button>
              {isBlendValid ?
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-500 text-white">
                  {FiCheckCircle({ className: "mr-1" })} Valid Blend
                </span> :
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-500 text-white">
                  {FiAlertCircle({ className: "mr-1" })} Incomplete
                </span>
              }
            </div>
          </div>

          <div className="mt-3">
            <div className="relative pt-1">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block text-primary-500">
                    Total: {totalPercentage.toFixed(1)}%
                  </span>
                </div>
                <div>
                  <span className="text-xs font-semibold inline-block text-primary-500">
                    Target: 100%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-neutral-200">
                <div
                  style={{ width: `${Math.min(totalPercentage, 100)}%` }}
                  className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ${getProgressBarClasses()}`}
                ></div>
              </div>
            </div>

            {/* Wine color indicators */}
            {totalPercentage > 0 && (
              <div className="flex mt-2 h-4 rounded overflow-hidden">
                {baseWines.map((wine, index) =>
                  wine.percentage > 0 && (
                    <div
                      key={wine.id}
                      style={{ width: `${(wine.percentage / totalPercentage) * 100}%` }}
                      className={`${wine.color} transition-all duration-500 ease-in-out`}
                      title={`${wine.name || `Component ${index + 1}`}: ${wine.percentage}%`}
                    ></div>
                  )
                )}
              </div>
            )}

            {validationError && (
              <div className="mt-1 flex items-center text-sm text-red-600">
                {FiAlertCircle({ className: "mr-1" })} {validationError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div className="flex items-center">
                <h2 className="text-lg leading-6 font-medium text-monday-text">Base Wines</h2>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {baseWines.length} Components
                </span>
              </div>
              <button
                type="button"
                onClick={addWine}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200 transition-colors duration-150"
              >
                {FiPlus({ className: "mr-2" })}
                Add Wine Component
              </button>
            </div>

            <div className="border-t border-monday-border-light">
              {baseWines.map((wine, index) => (
                <div
                  key={wine.id}
                  className={`px-4 py-5 sm:p-6 ${index !== 0 ? 'border-t border-monday-border-light' : ''}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full mr-2 ${wine.color}`}></div>
                      <h3 className="text-md font-medium text-monday-text">Component {index + 1}</h3>
                      {wine.name && (
                        <span className="ml-2 text-sm text-monday-text-muted">
                          ({wine.name})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center">
                      {wine.percentage > 0 && (
                        <span className="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-monday-header text-monday-text">
                          {wine.percentage}%
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleWineExpansion(index)}
                        className="mr-2 inline-flex items-center p-1 border border-transparent rounded-full text-primary-500 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200 transition-colors duration-150"
                      >
                        {wine.isExpanded
                          ? FiChevronUp({ className: "h-5 w-5" })
                          : FiChevronDown({ className: "h-5 w-5" })
                        }
                      </button>
                      {baseWines.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeWine(index)}
                          className="inline-flex items-center p-1 border border-transparent rounded-full text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150"
                        >
                          {FiTrash2({ className: "h-5 w-5" })}
                        </button>
                      )}
                    </div>
                  </div>

                  {wine.isExpanded && (
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="col-span-2">
                        <label htmlFor={`wine-name-${index}`} className="form-label flex items-center">
                          {FaWineGlass({ className: "mr-1 text-monday-text-muted" })}
                          Wine Name/Identifier
                        </label>
                        <input
                          type="text"
                          id={`wine-name-${index}`}
                          value={wine.name}
                          onChange={(e) => handleWineChange(index, 'name', e.target.value)}
                          placeholder="e.g., Cabernet Sauvignon 2020"
                          className="mt-1 block w-full border border-monday-border rounded-xl shadow-xs py-2 px-3 focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
                        />
                      </div>

                      <div>
                        <label htmlFor={`wine-alcohol-${index}`} className="form-label flex items-center">
                          {FaFlask({ className: "mr-1 text-monday-text-muted" })}
                          Alcohol (%)
                        </label>
                        <input
                          type="number"
                          id={`wine-alcohol-${index}`}
                          min="0"
                          max="30"
                          step="0.1"
                          value={wine.alcohol}
                          onChange={(e) => handleWineChange(index, 'alcohol', e.target.value)}
                          className="mt-1 block w-full border border-monday-border rounded-xl shadow-xs py-2 px-3 focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
                        />
                      </div>

                      <div>
                        <label htmlFor={`wine-ph-${index}`} className="form-label flex items-center">
                          {FaFlask({ className: "mr-1 text-monday-text-muted" })}
                          pH
                        </label>
                        <input
                          type="number"
                          id={`wine-ph-${index}`}
                          min="0"
                          max="14"
                          step="0.01"
                          value={wine.pH}
                          onChange={(e) => handleWineChange(index, 'pH', e.target.value)}
                          className="mt-1 block w-full border border-monday-border rounded-xl shadow-xs py-2 px-3 focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
                        />
                      </div>

                      <div>
                        <label htmlFor={`wine-ta-${index}`} className="form-label flex items-center">
                          {FaFlask({ className: "mr-1 text-monday-text-muted" })}
                          Total Acidity (g/L)
                        </label>
                        <input
                          type="number"
                          id={`wine-ta-${index}`}
                          min="0"
                          step="0.01"
                          value={wine.totalAcidity}
                          onChange={(e) => handleWineChange(index, 'totalAcidity', e.target.value)}
                          className="mt-1 block w-full border border-monday-border rounded-xl shadow-xs py-2 px-3 focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
                        />
                      </div>

                      <div>
                        <label htmlFor={`wine-rs-${index}`} className="form-label flex items-center">
                          {FaFlask({ className: "mr-1 text-monday-text-muted" })}
                          Residual Sugar (g/L)
                        </label>
                        <input
                          type="number"
                          id={`wine-rs-${index}`}
                          min="0"
                          step="0.1"
                          value={wine.residualSugar}
                          onChange={(e) => handleWineChange(index, 'residualSugar', e.target.value)}
                          className="mt-1 block w-full border border-monday-border rounded-xl shadow-xs py-2 px-3 focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm transition-all duration-150"
                        />
                      </div>

                      <div>
                        <label htmlFor={`wine-percentage-${index}`} className="form-label flex items-center">
                          {FaPercentage({ className: "mr-1 text-monday-text-muted" })}
                          Percentage in Blend (%)
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <input
                            type="number"
                            id={`wine-percentage-${index}`}
                            min="0"
                            max="100"
                            step="0.1"
                            value={wine.percentage}
                            onChange={(e) => handleWineChange(index, 'percentage', e.target.value)}
                            className={`block w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-primary-200 focus:border-primary-500 sm:text-sm ${
                              validationError ? 'border-red-300' : 'border-monday-border'
                            }`}
                          />
                          <div className="hidden sm:block absolute inset-y-0 right-0 flex items-center">
                            <div className="h-full flex items-center border-transparent bg-transparent py-0 pl-2 pr-4 text-monday-text-muted sm:text-sm">
                              %
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!wine.isExpanded && (
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      <div className="col-span-2 sm:col-span-1">
                        <span className="text-xs text-monday-text-muted">Alcohol:</span>
                        <span className="ml-1 text-xs font-medium">{wine.alcohol}%</span>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <span className="text-xs text-monday-text-muted">pH:</span>
                        <span className="ml-1 text-xs font-medium">{wine.pH}</span>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <span className="text-xs text-monday-text-muted">TA:</span>
                        <span className="ml-1 text-xs font-medium">{wine.totalAcidity} g/L</span>
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <span className="text-xs text-monday-text-muted">RS:</span>
                        <span className="ml-1 text-xs font-medium">{wine.residualSugar} g/L</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-monday-header px-4 py-4 sm:px-6 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-monday-text flex items-center">
                  {FaBalanceScale({ className: "mr-1" })}
                  Total Percentage:
                  <span className={`ml-2 font-bold ${totalPercentage === 100 ? 'text-green-600' : 'text-red-600'}`}>
                    {totalPercentage}%
                  </span>
                </span>
                {validationError && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    {FiAlertCircle({ className: "mr-1" })}
                    {validationError}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center px-3 py-2 border border-monday-border shadow-sm text-sm leading-4 font-medium rounded-md text-monday-text bg-white hover:bg-monday-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200 transition-colors duration-150"
              >
                {FiRefreshCw({ className: "mr-2" })}
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
              <div>
                <h2 className="text-lg leading-6 font-medium text-monday-text flex items-center">
                  {FaFlask({ className: "mr-2 text-primary-500" })}
                  Blend Results
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-monday-text-muted">
                  Calculated characteristics of the final blend
                </p>
              </div>
              {isCalculating && (
                <div className="animate-spin text-primary-500">
                  {FaSyncAlt({ size: 20 })}
                </div>
              )}
            </div>
            <div className="border-t border-monday-border-light px-4 py-5 sm:p-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-monday-text-muted flex items-center">
                    {FaFlask({ className: "mr-1" })}
                    Alcohol Content
                  </h3>
                  <div className="mt-2">
                    <div className="flex items-center">
                      <p className="text-3xl font-bold text-primary-500">
                        {blendResult.alcohol.toFixed(2)}%
                      </p>
                    </div>
                    <div className="w-full bg-neutral-200 rounded-full h-2.5 mt-2">
                      <div
                        className="bg-primary-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${Math.min(blendResult.alcohol * 6, 100)}%` }}>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-monday-text-muted flex items-center">
                    {FaFlask({ className: "mr-1" })}
                    pH
                  </h3>
                  <div className="mt-2">
                    <div className="flex items-center">
                      <p className="text-3xl font-bold text-primary-500">
                        {blendResult.pH.toFixed(2)}
                      </p>
                    </div>
                    <div className="w-full bg-neutral-200 rounded-full h-2.5 mt-2">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-500 ease-out ${
                          blendResult.pH < 3.0 ? 'bg-red-500' :
                          blendResult.pH > 4.0 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${(blendResult.pH / 14) * 100}%` }}>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-monday-text-muted flex items-center">
                      {FiInfo({ className: "mr-1" })}
                      Note: This is an approximation as pH is logarithmic
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-monday-text-muted flex items-center">
                    {FaFlask({ className: "mr-1" })}
                    Total Acidity (TA)
                  </h3>
                  <div className="mt-2">
                    <div className="flex items-center">
                      <p className="text-3xl font-bold text-primary-500">
                        {blendResult.totalAcidity.toFixed(2)} g/L
                      </p>
                    </div>
                    <div className="w-full bg-neutral-200 rounded-full h-2.5 mt-2">
                      <div
                        className="bg-primary-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${Math.min(blendResult.totalAcidity * 10, 100)}%` }}>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-monday-text-muted flex items-center">
                    {FaFlask({ className: "mr-1" })}
                    Residual Sugar (RS)
                  </h3>
                  <div className="mt-2">
                    <div className="flex items-center">
                      <p className="text-3xl font-bold text-primary-500">
                        {blendResult.residualSugar.toFixed(2)} g/L
                      </p>
                    </div>
                    <div className="w-full bg-neutral-200 rounded-full h-2.5 mt-2">
                      <div
                        className="bg-primary-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${Math.min(blendResult.residualSugar * 5, 100)}%` }}>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-primary-50 shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-monday-text flex items-center">
                {FiInfo({ className: "mr-2 text-primary-500" })}
                About the Calculator
              </h3>
              <div className="mt-2 max-w-xl text-sm text-monday-text-muted">
                <p>
                  This calculator uses weighted averages to estimate the final characteristics of your wine blend.
                </p>
                <p className="mt-2">
                  For accurate results, ensure the total percentage equals 100% and all component values are entered correctly.
                </p>
                <p className="mt-2">
                  Note that pH calculation is an approximation as true pH blending is more complex due to its logarithmic nature.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WineBlendCalculator;
